import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
} from 'antd';
import {PlusOutlined, InfoCircleOutlined, EditTwoTone} from '@ant-design/icons';
import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../common/hooks';
import {debounce} from 'lodash';
import {
  debounceDelay,
  sortbyFilter,
  statusFilter,
  statusFilterOptions,
} from '../../../../common/constants';
import {
  batteriesList,
  addBattery,
  updateBattery,
  pageNumberChange,
  pageSizeChange,
  searchBy,
  batteriesStatusFilter,
  sortBy,
  unregisteredBatteries,
  clearUnregistered,
} from './batteriesSlice';
import {ADD_BATTERY, BATTERY_DATA, UPDATE_BATTERY} from './batteries.type';
import checkFormUpdate from '../../../../utils/checkFormUpdate';
import {
  batteryCategoryList,
  clearBatteryCategoryFilters,
} from '../batteryCategory/batteryCategorySlice';
import {authStationsList, clearStationsFilters} from '../../station/stations/stationsSlice';
import {racksList} from '../../rack/racks/racksSlice';

const Batteries = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.batteries);
  const userPermission = useAppSelector((state) => state.config.config.permissions);
  const [isBatteryModalOpen, setIsBatteryModalOpen] = useState(false);
  const rackOptions = useAppSelector((state) => state.racks.racks.pageData);
  const [isRackDisable, setIsRackDisable] = useState(true);
  const [modalUpdate, setModalUpdate] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [batteryForm] = Form.useForm();
  const batteryCategoryOptions = useAppSelector(
    (state) => state.batteryCategory.batteryCategories.pageData,
  );
  const stationOptions = useAppSelector((state) => state.stations.stations.pageData);

  const [modalData, setModalData] = useState<BATTERY_DATA>({} as BATTERY_DATA);

  const handleupdateBattery = (values: UPDATE_BATTERY) => {
    const payload = checkFormUpdate(values, modalData);
    if (Object.keys(payload).length > 0) {
      dispatch(updateBattery({payload: payload, id: modalData.id}))
        .unwrap()
        .then(() => {
          setIsBatteryModalOpen(false);
          dispatch(
            batteriesList({
              page_size: state.batteries.page_size,
              page_number: state.batteries.page_number,
              sort_by: state.batteries.sort_by,
              ascending: state.batteries.ascending,
              search: state.batteries.search,
            }),
          );
        });
    } else {
      message.warning('No field Changed');
    }
  };

  const handleaddBattery = (values: ADD_BATTERY) => {
    dispatch(addBattery(values))
      .unwrap()
      .then(() => {
        handleAddBatteryModalClose();
        dispatch(
          batteriesList({
            page_size: state.batteries.page_size,
            page_number: state.batteries.page_number,
            sort_by: state.batteries.sort_by,
            ascending: state.batteries.ascending,
            search: state.batteries.search,
          }),
        );
      });
  };

  const handleAddBatteryModalClose = () => {
    setIsBatteryModalOpen(false);
    batteryForm.resetFields();
    dispatch(clearBatteryCategoryFilters());
    dispatch(clearStationsFilters());
    dispatch(clearUnregistered());
  };
  useEffect(() => {
    dispatch(
      batteriesList({
        page_size: state.batteries.page_size,
        page_number: state.batteries.page_number,
        sort_by: state.batteries.sort_by,
        ascending: state.batteries.ascending,
        search: state.batteries.search,
        is_active: state.batteries.is_active,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.batteries.page_number,
    state.batteries.page_size,
    state.batteries.search,
    state.batteries.is_active,
    state.batteries.sort_by,
    state.batteries.ascending,
  ]);

  const columns: ColumnsType<BATTERY_DATA> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return <>{(state.batteries.page_number - 1) * state.batteries.page_size + index + 1}</>;
      },
    },
    {
      title: 'Battery Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: 'Asset Id',
      dataIndex: 'asset_id',
      sorter: true,
    },
    {
      title: 'Category Name',
      dataIndex: 'category_name',
    },

    {
      title: ' Status',
      dataIndex: 'is_active',
      render: (_, record) => {
        return record.is_active ? (
          <Tag color='success'>Active</Tag>
        ) : (
          <Tag color='error'>Inactive</Tag>
        );
      },
    },
    {
      title: 'Home Station',
      dataIndex: 'station_name',
    },

    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            <InfoCircleOutlined
              style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
              onClick={() => {
                setModalData(record);
                setIsInfoModalOpen(true);
              }}
            />
            {userPermission.update_battery && (
              <EditTwoTone
                style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
                onClick={() => {
                  setModalData(record);
                  setModalUpdate(true);
                  batteryForm.setFieldsValue({is_active: record.is_active});
                  setIsBatteryModalOpen(true);
                }}
              />
            )}
          </>
        );
      },
    },
  ];

  const handleSearch = (e: string) => {
    if (e.length > 3) {
      dispatch(searchBy(e));
    } else if (e.length === 0) {
      dispatch(searchBy(e));
    }
  };

  const handleStationBouncing = debounce((e) => {
    dispatch(authStationsList({search: e, is_active: true}));
  }, debounceDelay);
  const handleBatteryCategoryBouncing = debounce((e) => {
    dispatch(batteryCategoryList({search: e, is_active: true}));
  }, debounceDelay);

  return (
    <div>
      <div
        style={{
          margin: '10px 0px 10px 0px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '350px  1fr   1fr',
        }}
      >
        <Input.Search
          placeholder='Search'
          defaultValue={state.batteries.search}
          style={{borderRadius: '10px'}}
          allowClear
          onSearch={(e) => handleSearch(e)}
        />
        <Select
          style={{width: '100px', marginLeft: '5px'}}
          value={
            state.batteries.is_active === undefined
              ? statusFilter.ALL
              : state.batteries.is_active === true
              ? statusFilter.ACTIVE
              : statusFilter.INACTIVE
          }
          options={statusFilterOptions}
          onChange={(value) => {
            if (value === statusFilter.ACTIVE) {
              dispatch(batteriesStatusFilter(true));
            } else if (value === statusFilter.ALL) {
              dispatch(batteriesStatusFilter(undefined));
            } else {
              dispatch(batteriesStatusFilter(false));
            }
          }}
          showArrow
        />
        <div style={{width: '100%', textAlign: 'end'}}>
          {userPermission.add_battery && (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              style={{borderRadius: '5px', marginInline: '5px'}}
              onClick={() => {
                setModalUpdate(false);
                dispatch(authStationsList({is_active: true}));
                dispatch(batteryCategoryList({is_active: true}));
                setIsBatteryModalOpen(true);
              }}
            >
              Add Battery
            </Button>
          )}
        </div>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        pagination={
          {
            total: state.batteries.totalDataCount,
            current: state.batteries.page_number,
            pageSize: state.batteries.page_size,
            showSizeChanger: true,
            // onChange: (pageNumber, pageSize) => {
            //   if (pageSize !== state.batteries.page_size) {
            //     dispatch(pageSizeChange({pageNumber, pageSize}));
            //   } else {
            //     dispatch(pageNumberChange(pageNumber));
            //   }
            // },
          } as TablePaginationConfig
        }
        loading={state.isLoading}
        size='middle'
        dataSource={state.batteries.pageData}
        scroll={{x: undefined, y: '69vh'}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(pagination, _, {order, field}: any) => {
          if (pagination.pageSize && pagination.pageSize !== state.batteries.page_size) {
            dispatch(pageSizeChange(pagination.pageSize));
          }
          if (pagination.current && pagination.current !== state.batteries.page_number) {
            dispatch(pageNumberChange(pagination.current));
          }
          if (field) {
            if (order === sortbyFilter.DESCEND) {
              dispatch(sortBy({ascending: false, sortBy: field}));
            } else {
              dispatch(sortBy({ascending: true, sortBy: field}));
            }
          }
        }}
      />
      <Modal
        title={`${modalData?.asset_id}`}
        open={isInfoModalOpen}
        onCancel={() => {
          setIsInfoModalOpen(false);
        }}
        footer={null}
        width={800}
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label='Batery AssetId' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.asset_id}
          </Descriptions.Item>
          <Descriptions.Item label='Batery Id' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.id}
          </Descriptions.Item>
          <Descriptions.Item label='Is Active' labelStyle={{fontWeight: 'bolder'}} span={2}>
            {modalData?.is_active ? (
              <Tag color='success'>Active</Tag>
            ) : (
              <Tag color='error'> Inactive</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label='Created By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.created_by}
          </Descriptions.Item>
          <Descriptions.Item label='Created At' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.created_at}
          </Descriptions.Item>
          <Descriptions.Item label='Updated By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.updated_by}
          </Descriptions.Item>
          <Descriptions.Item label='Updated At' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.updated_at}
          </Descriptions.Item>
          <Descriptions.Item label=' Battery Category ' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.category_name}
          </Descriptions.Item>
          <Descriptions.Item label=' Home Station ' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.station_name}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
      <Modal
        title={modalUpdate ? 'Update Battery' : 'Add Battery'}
        open={isBatteryModalOpen}
        maskClosable={false}
        onCancel={() => {
          // updateBatteryForm.resetFields();
          modalUpdate ? setIsBatteryModalOpen(false) : handleAddBatteryModalClose();
        }}
        okText={modalUpdate ? 'Update Battery' : 'Add Battery'}
        onOk={() => {
          batteryForm
            .validateFields()
            .then((values) => {
              //updateBatteryForm.resetFields();
              modalUpdate ? handleupdateBattery(values) : handleaddBattery(values);
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Row>
          <Col span={24}>
            <Form name='add_battery' form={batteryForm}>
              {modalUpdate ? (
                <Form.Item name='is_active' label='Is Active' valuePropName='checked'>
                  <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
                </Form.Item>
              ) : (
                <Row>
                  <Col span={24}>
                    <Row>
                      <Col span={11}>
                        <Form.Item
                          name='station_id'
                          rules={[{required: true, message: 'Please fill Station Id'}]}
                          label='Station'
                          labelCol={{span: 24}}
                        >
                          <Select
                            showSearch
                            placeholder='Select Station'
                            defaultActiveFirstOption={false}
                            showArrow={false}
                            filterOption={false}
                            onSearch={(e) => {
                              if (e.length > 3) handleStationBouncing(e);
                            }}
                            onChange={(e) => {
                              batteryForm.setFieldsValue({rack_id: undefined});

                              if (e.length < 1) {
                                batteryForm.setFieldsValue({rack_id: undefined});
                                setIsRackDisable(true);
                              } else {
                                dispatch(racksList({station_id: [e]}));
                                setIsRackDisable(false);
                              }
                            }}
                            notFoundContent={null}
                            //loading={roles.isLoading}
                          >
                            {stationOptions.map((x, i) => (
                              <Select.Option key={i} value={x.id}>
                                {x.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={11} offset={2}>
                        <Form.Item
                          name='rack_id'
                          rules={[{required: true, message: 'Please Select Cabinet'}]}
                          label='Cabinet'
                          labelCol={{span: 24}}
                        >
                          <Select
                            placeholder='Select Cabinet'
                            style={{width: '200px'}}
                            showArrow={false}
                            disabled={isRackDisable}
                            onChange={(e) => {
                              dispatch(unregisteredBatteries(e));
                            }}
                          >
                            {rackOptions.map((x, i) => (
                              <Select.Option key={i} value={x.id}>
                                {x.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={11}>
                        <Form.Item
                          name='category_id'
                          rules={[{required: true, message: 'Please fill catagorId'}]}
                          label='Catagory'
                          labelCol={{span: 24}}
                        >
                          <Select
                            placeholder=' Select Category '
                            showSearch
                            defaultActiveFirstOption={false}
                            showArrow={false}
                            filterOption={false}
                            onSearch={(e: string) => {
                              if (e.length > 3) handleBatteryCategoryBouncing(e);
                            }}
                            notFoundContent={null}
                            //loading={roles.isLoading}
                          >
                            {batteryCategoryOptions.map((x, i) => (
                              <Select.Option key={i} value={x.id}>
                                {x.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={11} offset={2}>
                        <Form.Item
                          name='asset_id'
                          rules={[{required: true, message: 'Please select Battery'}]}
                          label='Battery Id'
                          labelCol={{span: 24}}
                        >
                          <Select
                            placeholder='Select Battery'
                            showArrow={false}
                            filterOption={false}
                          >
                            {state.unregistered.map((x, i) => (
                              <Select.Option key={i} value={x.asset_id}>
                                {`${x.position + 1} - ${x.asset_id}`}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={11}>
                        <Form.Item
                          name='name'
                          rules={[
                            {required: true, message: 'Please fill Battery Name '},
                            {min: 4, message: 'Minimum length is 4 characters'},
                          ]}
                          label='Battery Name'
                          labelCol={{span: 24}}
                        >
                          <Input autoComplete='off' placeholder='Battery Name' />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default Batteries;
